import { deepMerge } from '../i18n/resolve';
import localeEn from '../i18n/locales/en';
import localeMr from '../i18n/locales/mr';

const translationsData = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      brandName: 'Gadkot',
      forts: 'Forts',
      allForts: 'All Forts',
      audioForts: 'Audio Forts',
      temples: 'Temples',
      blog: 'Blog',
      language: 'Language',
      english: 'English',
      marathi: 'मराठी'
    },

    // Home Page
    home: {
      welcome: 'Welcome to Gadkot',
      subtitle: 'Discover the rich heritage of Maharashtra through its magnificent forts and sacred temples',
      exploreForts: 'Explore Forts',
      audioTours: 'Audio Tours',
      visitTemples: 'Visit Temples',
      whyChoose: 'Why Choose Gadkot?',
      historicalPlaces: 'Historical Places',
      historicalPlacesDesc: 'Explore centuries of history through Maharashtra\'s iconic forts and temples',
      curatedInfo: 'Curated Information',
      curatedInfoDesc: 'Detailed information about each location including history, features, and best visiting times',
      travelPlanning: 'Travel Planning',
      travelPlanningDesc: 'Plan your visits with our comprehensive guides and seasonal recommendations',
      startExploring: 'Start Exploring',
      historicalForts: 'Historical Forts',
      historicalFortsDesc: 'Discover the majestic forts that shaped Maharashtra\'s history',
      exploreFortsAction: 'Explore Forts',
      audioToursDesc: 'Experience forts with immersive audio guides and narration',
      startAudioTour: 'Start Audio Tour',
      sacredTemples: 'Sacred Temples',
      sacredTemplesDesc: 'Visit the divine temples that showcase spiritual heritage',
      visitTemplesAction: 'Visit Temples',
      footerTitle: 'Gadkot',
      footerDesc: 'Preserving and showcasing Maharashtra\'s heritage',
      exploreHeritage: 'Explore Heritage',
      exploreHeritageSubtitle: 'Discover India\'s rich historical legacy through our curated collections',
      geoExplorerDesc: 'Discover sites across regions and themes',
      geoExplorerCta: 'Explore',
      thematicRoutesDesc: 'Curated journeys through history',
      thematicRoutesCta: 'Discover',
      historicalFortsTitle: 'Historical Forts',
      historicalFortsDesc: 'Explore magnificent fortresses',
      historicalFortsCta: 'Visit',
      sacredTemplesTitle: 'Sacred Temples',
      sacredTemplesDesc: 'Experience spiritual heritage',
      sacredTemplesCta: 'Worship',
      featuredFortsTitle: 'Featured Forts',
      featuredFortsSubtitle: 'Explore three remarkable forts from Maharashtra\'s heritage',
      viewAllForts: 'View All Forts',
      viewFort: 'View Fort',
      loadingForts: 'Loading forts…',
      noFeaturedForts: 'No forts available right now. Check back soon.',
      fortLoadError: 'Could not load featured forts',
      searchSites: 'Search Sites',
      learnMore: 'Learn More',
      contactUs: 'Contact Us',
      nearbyFortsTitle: 'Nearby Forts',
      nearbyFortsShowing: 'forts shown',
      nearbyFortsEmpty: 'No forts with map coordinates nearby — try another town or open the full map.',
      nearbyFortsCta: 'Explore All Nearby Forts',
      nearbyFortsDesc: 'Find forts near your location with real distances',
      nearbyFortsCtaShort: 'Find nearby',
    },

    // Forts Page
    forts: {
      title: 'Historical Forts',
      subtitle: 'Explore the majestic forts of Maharashtra',
      built: 'Built',
      height: 'Height',
      keyFeatures: 'Key Features',
      bestTime: 'Best time',
      audioGuideAvailable: 'Audio Guide Available'
    },

    // All Forts Page
    allForts: {
      title: 'All Forts',
      subtitle: 'Explore the complete collection of Maharashtra forts',
      fortsFound: 'forts found',
      searchPlaceholder: 'Search forts by name, location, or description...',
      allLocations: 'All Locations',
      sortByName: 'Sort by Name',
      sortByLocation: 'Sort by Location',
      sortByHeight: 'Sort by Height',
      sortByYear: 'Sort by Year',
      sortByDifficulty: 'Sort by Difficulty',
      sortByPopularity: 'Sort by Popularity',
      noFortsFound: 'No forts found',
      tryDifferentSearch: 'Try a different search term or filter',
      viewDetails: 'View Details',
      advancedFilters: 'Advanced Filters',
      clearFilters: 'Clear Filters',
      difficulty: 'Difficulty Level',
      allDifficulties: 'All Difficulties',
      fortType: 'Fort Type',
      allTypes: 'All Types',
      region: 'Region',
      allRegions: 'All Regions',
      heightRange: 'Height Range',
      yearRange: 'Year Range',
      gridView: 'Grid View',
      mapView: 'Map View',
      trendingForts: 'Trending Forts',
      recentlyVisited: 'Recently Visited',
      visits: 'visits',
      nearbyForts: 'Nearby Forts',
      mapListTitle: 'Forts on map',
      mapHint: 'Click a marker or list item to locate the fort. Open the detail page for trek info and history.'
    },

    nearbyForts: {
      title: 'Nearby Forts',
      heritageBadge: 'Sahyadri • Heritage Atlas',
      subtitle: '“I’m in {{place}} — what’s close?” Real fort distances from your location.',
      subtitlePending: 'Allow location or pick a town — discover forts near you.',
      mapTitle: 'Heritage map — forts near you',
      mapLegend: 'Parchment survey · forts in radius',
      mapFootnote: 'Distances are straight-line estimates from fort coordinates in the Gadkot archive.',
      fortMarker: 'Fort',
      useMyLocation: 'Use my location',
      myLocation: 'My current location',
      locationPromptTitle: 'Find forts near you',
      locationPromptBody: 'We use your device location once to show real distances to nearby forts on the map. Location is only used for this list — it is not saved or shared.',
      locationPromptHint: 'Tap the button below — your browser will ask for permission.',
      locationRequesting: 'Waiting for location permission…',
      youAreHere: 'You are here',
      placePlaceholder: 'Search a town — e.g. Lonavala, Pune, Nashik…',
      searchPlace: 'Search place',
      placeNotFound: 'Place not found — try a nearby town from the chips above.',
      geoUnsupported: 'Location is not supported in this browser.',
      geoDenied: 'Could not get GPS — pick a town or search below.',
      detecting: 'Detecting your location…',
      loadingForts: 'Loading nearby forts…',
      loadError: 'Could not load nearby forts',
      fortsFound: 'forts nearby',
      radius: 'Radius',
      category: 'Type',
      allTypes: 'All types',
      hillFort: 'Hill forts',
      seaFort: 'Sea forts',
      coastalFort: 'Coastal forts',
      sortBy: 'Sort',
      sortDistance: 'Nearest first',
      sortName: 'Name',
      sortDifficulty: 'Difficulty',
      directions: 'Directions',
      viewDetails: 'View details',
      noneFound: 'No forts in this radius',
      noneFoundHint: 'No forts with map coordinates within {{radius}} km. Try a larger radius or another town.',
      increaseRadius: 'Search farther',
      footerNote: 'Distances are straight-line estimates from Gadkot fort coordinates. Always verify trek access locally.',
      places: {
        lonavala: 'Lonavala',
        pune: 'Pune',
        mumbai: 'Mumbai',
        nashik: 'Nashik',
        mahabaleshwar: 'Mahabaleshwar',
        kolhapur: 'Kolhapur',
        satara: 'Satara',
        aurangabad: 'Aurangabad',
      },
    },

    // Fort Detail Page
    fortDetail: {
      notFound: 'Fort Not Found',
      backToForts: 'Back to All Forts',
      heritageBadge: 'Sahyadri • Fort • Heritage',
      about: 'About This Fort',
      keyInformation: 'Key Information',
      visitingHours: 'Visiting Hours',
      audioGuideAvailable: 'Audio Guide Available',
      audioGuideDescription: 'Listen to the fascinating history and stories of this fort',
      startAudioGuide: 'Start Audio Guide',
      quickInfo: 'Quick Info',
      location: 'Location',
      builtIn: 'Built In',
      elevation: 'Elevation',
      bestTimeToVisit: 'Best Time to Visit',
      audioGuide: 'Audio Guide',
      addFavorite: 'Add to Favorites',
      removeFavorite: 'Remove from Favorites',
      share: 'Share',
      historicalTimeline: 'Historical Timeline',
      storiesLegends: 'Stories & Legends',
      photographyTips: 'Photography Tips',
      photographyTip1: 'Golden hour (sunrise/sunset) provides the best lighting',
      photographyTip2: 'Wide-angle lenses capture the fort\'s grandeur',
      photographyTip3: 'Don\'t miss the panoramic views from the top',
      richMedia: 'Rich Media Experiences',
      virtualTour: 'Virtual Tour',
      videoDocumentary: 'Video Documentary',
      architectureDetails: 'Architecture Details',
      navLocationFeatures: 'Navigation & Location Features',
      gpsNavigation: 'GPS Navigation',
      arView: 'AR View',
      offlineMaps: 'Offline Maps',
      nearbyPlaces: 'Nearby Places',
      signInForFavorites: 'Please sign in to add forts to your favorites.',
      noKeyFeatures: 'No key features listed for this fort yet. Check access information and description above.',
      audioGuidesTitle: 'Multilingual Audio Guide',
      multimediaExperiences: 'Multimedia Experiences',
      audioGuides: 'Audio Guides',
      qrScanner: 'QR Scanner',
      qrScannerTitle: 'QR Code Scanner',
      virtualTour360Short: '360° Tour',
      reenactments: 'Historic Reconstruction',
      communitySocialFeatures: 'Community & Social Features',
      photoCommunity: 'Photo Community',
      reviews: 'Reviews',
      reviewsTitle: 'Reviews & Ratings',
      checkIn: 'Check-in',
      checkInTitle: 'Check-in System',
      socialShare: 'Social Share',
      socialShareTitle: 'Social Integration',
      myProfile: 'My Profile',
    },

    multilingualAudioGuide: {
      transcript: 'Transcript',
      selectLanguage: 'Select Language',
      langEnglish: 'English',
      langMarathi: 'Marathi',
      langHindi: 'Hindi',
      minutes: '{{count}} minutes',
      chapterMin: '{{count}} min',
      online: 'Online',
      offline: 'Offline',
      ttsHint: 'Narration uses your device\'s text-to-speech (no MP3 file required).',
      chapters: 'Chapters',
      speed: 'Speed:',
      downloading: 'Downloading... {{progress}}%',
      saveTranscript: 'Save transcript ({{size}})',
      shareGuide: 'Share Guide',
      moreFeatures: 'More Features',
      historicalContext: 'Historical Context',
      historicalContextDesc: 'Deep dive into the historical significance and timeline of events',
      expertCommentary: 'Expert Commentary',
      expertCommentaryDesc: 'Additional insights from historians and archaeologists',
      locationStories: 'Location-Based Stories',
      locationStoriesDesc: 'Stories specific to different areas within the fort',
      interactiveQuiz: 'Interactive Quiz',
      interactiveQuizDesc: 'Test your knowledge with questions about the fort',
      errors: {
        noContent: 'No guide content available for this chapter.',
        playFailed: 'Unable to play audio guide. Please try another language or browser.',
        ttsUnsupported: 'Text-to-speech is not supported in this browser.'
      }
    },

    timeline: {
      close: 'Close',
      selectEvent: 'Click an event to see details',
      year: 'Year',
      impact: 'Impact',
      moreDetails: 'More details',
      eraOf: 'Era',
      eventsCount: 'events',
      navPrev: 'Previous era',
      navNext: 'Next era',
      defaultSubtitle: 'A journey through the highs and lows of the Maratha Empire',
      raigadSubtitle: 'The journey of Chhatrapati Shivaji Maharaj\'s capital',
      rangeDefault: '1600 – 1818',
      rangeRaigad: '1647 – present',
    },

    gpsNavigation: {
      navigateTo: 'Navigate to {{name}}',
      distance: 'Distance',
      duration: 'Duration',
      steps: 'Steps',
      yourLocation: 'Your location',
      accuracy: 'Accuracy',
      viewDetails: 'View details',
      toggleNearby: 'Show nearby places',
      recalculateRoute: 'Recalculate route',
      nearbyPlaces: 'Nearby places',
      startNavigation: 'Start navigation',
      stopNavigation: 'Stop navigation',
      trackingActive: 'Tracking active',
      continueStraight: 'Continue straight',
      followRoute: 'Follow the route',
      locationError: 'Location error',
      retry: 'Retry',
      close: 'Close',
      loading: 'Loading map…',
      placeTypes: {
        restaurant: 'Restaurant',
        hotel: 'Hotel',
        parking: 'Parking',
      },
    },

    arView: {
      experienceTitle: 'AR Historical Experience',
      experienceHint: 'Point your camera at the fort to see historical overlays and reconstructions',
      startExperience: 'Start AR experience',
      stopExperience: 'Stop AR experience',
      webxrFallback: 'Note: WebXR not supported. Using camera overlay mode.',
      cameraError: 'Unable to access camera. Please check permissions.',
      howToUseTitle: 'How to use AR view',
      howToUse1: 'Point your camera at the fort or location',
      howToUse2: 'Move your device to see different angles',
      howToUse3: 'Use arrow buttons to switch between historical periods',
      howToUse4: 'The overlay adjusts based on your device orientation',
      howToUse5: 'For best results, stand at a good distance from the fort',
      overlayCounter: '{{current}} / {{total}}',
      overlayOriginalTitle: 'Original structure',
      overlayOriginalDesc: 'See how {{name}} looked when it was first built in {{year}}',
      overlayStrategicTitle: 'Strategic view',
      overlayStrategicYear: 'Historical period',
      overlayStrategicDesc: 'View the strategic importance and military layout of the fort',
      overlayBattleTitle: 'Battle scene',
      overlayBattleYear: 'Historical battle',
      overlayBattleDesc: 'Visualize important historical battles that took place here',
      prevOverlay: 'Previous overlay',
      nextOverlay: 'Next overlay',
    },

    offlineMaps: {
      subtitle: 'Download maps for offline access',
      onlineReady: 'Online — ready to download',
      offlineUsing: 'Offline — using downloaded maps',
      storageUsage: 'Storage: {{used}}MB / {{total}}MB used',
      storageWarning: 'Storage space is running low. Consider removing some maps.',
      downloadedMaps: 'Downloaded maps ({{count}})',
      clearAll: 'Clear all',
      clearAllConfirm: 'Are you sure you want to delete all offline maps?',
      mapMeta: '{{size}}MB • Downloaded {{date}}',
      totalDownloaded: 'Total downloaded: {{size}}MB',
      availableMaps: 'Available maps',
      estimatedSize: 'Estimated size: ~15MB',
      downloaded: 'Downloaded',
      download: 'Download',
      downloadFailed: 'Failed to download map. Please try again.',
      needInternet: 'You need an internet connection to download maps',
      tipsTitle: 'Tips for offline maps',
      tips1: 'Download maps when you have a stable internet connection',
      tips2: 'Download maps for areas you plan to visit without internet',
      tips3: 'Maps include navigation and nearby attraction information',
      tips4: 'Regularly update maps to get the latest information',
    },

    nearbyAttractions: {
      aroundFort: 'Around {{name}}',
      searchPlaceholder: 'Search nearby places…',
      placesFound: '{{count}} places found',
      sortDistance: 'Sort by distance',
      sortName: 'Sort by name',
      sortRating: 'Sort by rating',
      noPlacesFound: 'No nearby places found',
      loading: 'Loading nearby places…',
      directions: 'Directions',
      call: 'Call',
      away: '{{distance}} away',
      cuisine: 'Cuisine',
      hours: 'Hours',
      website: 'Website',
      visitWebsite: 'Visit website',
      showMap: 'Show map',
      hideMap: 'Hide map',
      getDirections: 'Get directions',
      rating: '{{rating}} rating',
      categories: {
        all: 'All places',
        restaurant: 'Restaurants',
        hotel: 'Hotels',
        parking: 'Parking',
        cafe: 'Cafes',
        shopping: 'Shopping',
        fuel: 'Gas stations',
        attraction: 'Attractions',
      },
    },

    qrScanner: {
      history: 'History',
      scanHistory: 'Scan history',
      startScan: 'Start scanning',
      readyToScan: 'Ready to scan',
      stopScan: 'Stop',
      pointCamera: 'Point your camera at a QR code at the fort',
      idleDescription: 'Scan QR codes at the fort to get instant information, stories, and multimedia content',
      scanning: 'Scanning…',
      scanSuccess: 'Scan successful',
      scanFailed: 'Could not read QR code. Try again.',
      noHistory: 'No scan history yet',
      noScansYet: 'No scans yet. Start scanning QR codes to see history.',
      offline: 'Offline',
      online: 'Online',
      signInRequired: 'Please sign in to save scans',
      whatYouCanScan: 'What you can scan',
      howToUse: 'How to use',
      featureHistoricalTitle: 'Historical information',
      featureHistoricalDesc: 'Detailed history and significance of structures',
      featureNavigationTitle: 'Navigation guides',
      featureNavigationDesc: 'Directions and paths within the fort',
      featureStoriesTitle: 'Stories & legends',
      featureStoriesDesc: 'Myths and tales associated with the fort',
      featureAudioTitle: 'Audio guides',
      featureAudioDesc: 'Narrated tours in multiple languages',
      step1: 'Look for QR code plaques at different locations within the fort',
      step2: 'Tap "Start scanning" and point your camera at the QR code',
      step3: 'Wait for the scan to complete and enjoy the content',
      step4: 'Share, bookmark, or download content for offline access',
      audioGuideAvailable: 'Audio guide available',
      videoTourAvailable: 'Video tour available',
      share: 'Share',
      download: 'Download',
      cameraGranted: 'Camera allowed',
      cameraDenied: 'Camera denied',
      cameraPrompt: 'Camera permission needed',
      categories: {
        historical: 'Historical',
        navigation: 'Navigation',
        legend: 'Legend',
        architecture: 'Architecture',
        information: 'Information',
      },
    },

    virtualTour360: {
      scenes: 'Scenes',
      dragToExplore: 'Drag to explore the panorama',
      sceneInfo: 'Scene information',
      mapView: 'Map view',
    },

    historicalReenactments: {
      pageTitle: 'Historical reenactments',
      pageDescription:
        'Experience the rich history of {{name}} through carefully crafted historical reenactments. Watch dramatic depictions of battles, ceremonies, and daily life from different eras.',
      searchPlaceholder: 'Search videos, tags, or descriptions…',
      offline: 'Offline',
      online: 'Online',
      allVideos: 'All videos',
      battles: 'Battles',
      dailyLife: 'Daily life',
      ceremonies: 'Ceremonies',
      documentaries: 'Documentaries',
      noVideos: 'No videos found',
      watchNow: 'Watch now',
      relatedVideos: 'Related videos',
      views: '{{count}} views',
      tags: 'Tags',
      categories: {
        battle: 'Battle',
        lifestyle: 'Daily life',
        ceremony: 'Ceremony',
        documentary: 'Documentary',
      },
    },

    photoCommunity: {
      upload: 'Upload',
      searchPlaceholder: 'Search photos, tags, or users…',
      sortRecent: 'Recent',
      sortPopular: 'Popular',
      sortViews: 'Most viewed',
      sortComments: 'Most commented',
      filterAll: 'All',
      filterFeatured: 'Featured',
      filterFollowing: 'Following',
      noPhotos: 'No photos yet',
      beFirst: 'Be the first to share a photo of {{name}}!',
      uploadFirst: 'Upload first photo',
      signInUpload: 'Please sign in to upload photos',
      likes: '{{count}} likes',
      comments: '{{count}} comments',
      loadError: 'Could not load photos. Please try again.',
      retry: 'Retry',
      loading: 'Loading photos…',
      followingUnavailable: 'Following feed is not available yet.',
      featuredBadge: 'Featured',
      uploadModalTitle: 'Upload photo',
      captionPlaceholder: 'Add a caption (optional)…',
      shareAtFort: 'Share your experience at {{name}}',
      choosePhoto: 'Choose photo',
      fileHint: 'JPG or PNG, up to 10MB',
      uploading: 'Uploading…',
      uploadSuccess: 'Photo submitted! It will appear after moderation.',
      uploadFailed: 'Failed to upload photo. Please try again.',
      today: 'Today',
      yesterday: 'Yesterday',
      daysAgo: '{{count}} days ago',
      weeksAgo: '{{count}} weeks ago',
      linkCopied: 'Link copied to clipboard!',
      anonymousUser: 'Visitor',
    },

    reviewsRatings: {
      writeReview: 'Write review',
      searchPlaceholder: 'Search reviews…',
      reviewsCount: '{{count}} reviews',
      sortRecent: 'Most recent',
      sortHelpful: 'Most helpful',
      sortRatingHigh: 'Highest rating',
      sortRatingLow: 'Lowest rating',
      filterAll: 'All',
      filterVerified: 'Verified',
      filterWithPhotos: 'With photos',
      helpful: 'Helpful',
      verified: 'Verified visit',
      signInReview: 'Please sign in to write a review.',
      submitReview: 'Submit review',
      noReviews: 'No reviews yet',
      noReviewsHint: 'Share parking, steps, crowd, and best time — it helps families plan visits.',
      yourReview: 'Your review',
      editReview: 'Edit your review',
      ratingLabel: 'Rating',
      titleLabel: 'Title (optional)',
      commentLabel: 'Your experience',
      visitDateLabel: 'Visit date (optional)',
      titlePlaceholder: 'e.g. Easy trek, crowded on Sunday',
      commentPlaceholder: 'Parking, steps, crowd, best time to visit…',
      confirmDelete: 'Delete your review permanently?',
      loadError: 'Could not load reviews.',
      submitError: 'Could not save review. Please try again.',
      deleteError: 'Could not delete review.',
      visitedOn: 'Visited {{date}}',
      today: 'Today',
      yesterday: 'Yesterday',
      daysAgo: '{{count}} days ago',
      weeksAgo: '{{count}} weeks ago',
    },

    checkInSystem: {
      passport: 'Passport',
      totalCheckIns: 'Total check-ins',
      dayStreak: 'Day streak',
      uniqueForts: 'Unique forts',
      achievements: 'Achievements',
      alreadyCheckedIn: 'Already checked in!',
      checkedInToday: 'You have already checked in at {{name}} today.',
      lastCheckIn: 'Last check-in:',
      readyToCheckIn: 'Ready to check in?',
      checkInPrompt: 'Add this fort to your passport and track your journey!',
      checkInNow: 'Check in now',
      passportSoon: 'Passport download coming soon!',
      signInRequired: 'Please sign in to check in',
    },

    socialIntegration: {
      connect: 'Connect',
      totalShares: 'Total shares',
      socialLikes: 'Social likes',
      comments: 'Comments',
      mentions: 'Mentions',
      connectedAccounts: 'Connected accounts',
      notConnected: 'Not connected',
      followers: '{{count}} followers',
      shareExperience: 'Share your experience',
      copyLink: 'Copy link',
      linkCopied: 'Link copied!',
      shareNow: 'Share now',
      connectAccounts: 'Connect accounts',
    },

    userProfile: {
      overview: 'Overview',
      achievements: 'Achievements',
      activity: 'Activity',
      stats: 'Statistics',
      fortsVisited: 'Forts visited',
      photosShared: 'Photos shared',
      reviewsWritten: 'Reviews written',
      badgesEarned: 'Badges earned',
      editProfile: 'Edit profile',
      saveProfile: 'Save',
      cancelEdit: 'Cancel',
      signInRequired: 'Please sign in to view your profile',
      memberSince: 'Member since {{date}}',
    },

    fortMedia: {
      close: 'Close',
      chapters: 'Chapters',
      storiesSubtitle: 'Historical stories',
      storiesCount: 'stories',
      storyDetails: 'Story details',
      category: 'Category',
      timePeriod: 'Period',
      totalDuration: 'Total duration',
      minutes: 'minutes',
      relatedStories: 'Related stories',
      characters: 'Characters',
      moralTitle: 'Moral of the story',
      previous: 'Previous',
      next: 'Next',
      playAudio: 'Play audio',
      pauseAudio: 'Pause audio',
      play: 'Play',
      pause: 'Pause',
      audioNarration: 'Audio narration',
      architectureTitle: 'Architectural Details',
      view3d: '3D View',
      viewBlueprint: 'Blueprint',
      viewTechnique: 'Construction Method',
      model3d: '3D Model',
      controls3d: '3D Controls',
      autoRotate: 'Auto Rotate',
      stopRotation: 'Stop Rotation',
      keyFeatures: 'Key Features',
      dimensions: 'Dimensions',
      sections: 'Sections',
      blueprintView: 'Blueprint View',
      blueprintHint: 'Interactive blueprint will appear here',
      constructionMethod: 'Construction Method',
      materials: 'Materials',
      tools: 'Tools',
      timeline: 'Timeline',
      constructionProcess: 'Construction Process',
      processFoundation: 'Foundation excavation and preparation',
      processStone: 'Stone cutting and shaping',
      processWall: 'Wall construction and reinforcement',
      processRoof: 'Roof and final finishing',
      dimWidth: 'Width',
      dimHeight: 'Height',
      dimDepth: 'Depth',
      dimPerimeter: 'Perimeter',
      dimArea: 'Area',
    },

    // Audio Forts Page
    audioForts: {
      title: 'Audio Forts',
      subtitle: 'Experience Maharashtra\'s forts with immersive audio guides',
      availableCount: 'Forts with Audio Guides Available',
      noAudioGuides: 'No Audio Guides Available',
      noAudioGuidesDesc: 'Currently, there are no forts with audio guides. Check back soon!',
      audioGuideSections: 'Audio Guide Sections',
      startAudioTour: 'Start Audio Tour'
    },

    // Temples Page
    temples: {
      title: 'Sacred Temples',
      subtitle: 'Discover the spiritual heritage of Maharashtra',
      deity: 'Deity',
      keyFeatures: 'Key Features',
      bestTime: 'Best time'
    },

    // Audio Guide
    audioGuide: {
      title: 'Audio Guide',
      subtitle: 'Smart digital heritage audio experience',
      poweredBy: 'Developed by Gadkot',
      sections: {
        introduction: 'Introduction',
        history: 'History',
        architecture: 'Architecture',
        significance: 'Significance',
        exploration: 'Exploration',
        conclusion: 'Conclusion'
      },
      copyright: '© Gadkot — Maharashtra Heritage Platform'
    },

    // Common
    common: {
      meters: 'meters',
      yearBuilt: 'Built',
      location: 'Location',
      description: 'Description',
      features: 'Features'
    },

    // Footer
    footer: {
      title: 'Gadkot',
      description: 'Preserving and showcasing Maharashtra\'s heritage',
      contact: 'Contact Us',
      followUs: 'Follow Us',
      socialTagline: 'Explore Maharashtra\'s rich heritage',
      rights: '@gadkot',
      allRightsReserved: 'All Rights Reserved',
      privacyPolicy: 'Privacy Policy',
      termsOfService: 'Terms of Service'
    },

    // About Page
    about: {
      title: 'About Gadkot',
      subtitle: 'Digital Gateway to India\'s Historical Heritage',
      whatIsGadkot: 'What is Gadkot?',
      whatIsGadkotDesc: 'Gadkot is a comprehensive digital heritage platform dedicated to preserving, showcasing, and promoting India\'s rich historical legacy. We bring centuries of history to your fingertips through immersive digital experiences.',
      primaryGoals: 'Primary Goals',
      goal1: 'Democratize access to historical knowledge',
      goal2: 'Preserve cultural heritage through digital documentation',
      goal3: 'Create engaging educational experiences',
      goal4: 'Connect people with their historical roots',
      targetAudience: 'Target Audience',
      audience1: 'History enthusiasts and researchers',
      audience2: 'Students and educators',
      audience3: 'Travelers and tourists',
      audience4: 'Cultural heritage preservationists',
      mission: 'Our Mission',
      missionAndVisionTitle: 'Our Mission and Our Vision',
      missionDesc: 'To preserve and promote India\'s historical heritage through innovative digital solutions, making history accessible and engaging for everyone.',
      vision: 'Our Vision',
      visionDesc: 'To become the world\'s most comprehensive digital platform for historical heritage, connecting millions with their cultural roots.',
      values: 'Our Values',
      value1: 'Preservation of cultural heritage',
      value2: 'Educational excellence',
      value3: 'Innovation in digital storytelling',
      value4: 'Community engagement',
      achievements: 'Achievements',
      achievement1: 'Historical focus and educational content',
      achievement2: 'Digital preservation initiatives',
      achievement3: 'Mission-driven approach',
      achievement4: 'Community building efforts',
      story: 'Our Story',
      storyDesc: 'Founded in 2024, Gadkot emerged from a passion for history and technology. We are a new platform embarking on our mission to preserve and showcase India\'s magnificent heritage for future generations.',
      joinUs: 'Join Our Journey',
      joinUsDesc: 'Together, let\'s preserve and celebrate the rich tapestry of India\'s historical heritage.'
    },

    // Contact Page
    contact: {
      title: 'Contact Us',
      subtitle: 'Get in touch with the Gadkot team',
      getInTouch: 'Get in Touch',
      contactDesc: 'We\'d love to hear from you. Whether you have questions, feedback, or collaboration opportunities, feel free to reach out.',
      formName: 'Name',
      formEmail: 'Email',
      formSubject: 'Subject',
      formMessage: 'Message',
      formSubmit: 'Send Message',
      departments: 'Departments',
      supportDept: 'Support Department',
      supportDesc: 'For technical assistance and account-related queries',
      supportEmail: 'support@gadkot.com',
      contentDept: 'Content Department',
      contentDesc: 'For content suggestions and historical information',
      contentEmail: 'content@gadkot.com',
      partnershipDept: 'Partnership Department',
      partnershipDesc: 'For collaboration and partnership opportunities',
      partnershipEmail: 'partnerships@gadkot.com',
      officeHours: 'Office Hours',
      officeDays: 'Monday - Friday: 9:00 AM - 6:00 PM',
      weekend: 'Saturday - Sunday: Closed',
      faq: 'Frequently Asked Questions',
      faq1: 'How can I contribute historical information?',
      faq1Ans: 'You can contribute by reaching out to our content team with verified historical information, photographs, or documents.',
      faq2: 'Do you offer guided tours?',
      faq2Ans: 'We currently offer digital audio guides. Physical guided tours may be available in partnership with local authorities.',
      faq3: 'How accurate is your historical information?',
      faq3Ans: 'We work with historians and archaeological authorities to ensure the highest accuracy of all historical content.',
      socialMedia: 'Connect With Us',
      socialDesc: 'Follow us on social media for updates, historical facts, and community discussions.'
    },

    // Blog Page
    blog: {
      title: 'Heritage Blog',
      subtitle: 'Stories, insights, and discoveries from India\'s historical legacy',
      searchPlaceholder: 'Search blogs by title, content, or tags...',
      categoryFilter: 'All Categories',
      featuredPost: 'Featured Post',
      latestPosts: 'Latest Posts',
      readMore: 'Read More',
      publishedOn: 'Published on',
      readingTime: 'min read',
      authorBy: 'By',
      addBlog: 'Add Blog',
      blogTitle: 'Blog Title',
      blogContent: 'Blog Content',
      blogCategory: 'Category',
      blogTags: 'Tags (comma separated)',
      blogImages: 'Images (URLs, one per line)',
      submitBlog: 'Submit Blog',
      submitForReview: 'Submit for Review',
      reviewNotice: 'Your blog will be reviewed within 24-48 hours.',
      categories: {
        forts: 'Forts',
        temples: 'Temples',
        historical: 'Historical Sites',
        culture: 'Culture',
        travel: 'Travel'
      },
      noBlogsFound: 'No blogs found',
      tryDifferentSearch: 'Try different search terms or categories',
      views: 'views',
      likes: 'likes',
      comments: 'comments'
    },

    // Search Results Page
    search: {
      title: 'Search Results',
      searchingFor: 'Searching for',
      resultsFound: 'results found',
      noResults: 'No results found',
      noResultsDesc: 'Try adjusting your search terms or filters',
      filters: 'Filters',
      allTypes: 'All Types',
      fortsType: 'Forts',
      templesType: 'Temples',
      routesType: 'Routes',
      sortBy: 'Sort by',
      relevance: 'Relevance',
      name: 'Name',
      popularity: 'Popularity',
      recentlyAdded: 'Recently Added',
      clearFilters: 'Clear Filters',
      searchIn: 'Search in',
      allContent: 'All Content',
      titles: 'Titles',
      descriptions: 'Descriptions',
      tags: 'Tags'
    },

    // Error Pages
    error: {
      notFound: 'Page Not Found',
      notFoundDesc: 'The page you\'re looking for doesn\' exist or has been moved.',
      serverError: 'Server Error',
      serverErrorDesc: 'Something went wrong on our end. Please try again later.',
      forbidden: 'Access Denied',
      forbiddenDesc: 'You don\'t have permission to access this page.',
      serviceUnavailable: 'Service Unavailable',
      serviceUnavailableDesc: 'We\'re currently down for maintenance. Please check back soon.',
      backToHome: 'Back to Home',
      tryAgain: 'Try Again',
      reportIssue: 'Report Issue',
      quickLinks: 'Quick Links',
      exploreForts: 'Explore Forts',
      visitTemples: 'Visit Temples',
      readBlog: 'Read Blog',
      contactSupport: 'Contact Support'
    },

    // Geographic Detail Page
    geographic: {
      rajasthan: {
        title: 'Rajasthan Heritage',
        subtitle: 'Land of Kings and Magnificent Forts'
      },
      delhi: {
        title: 'Delhi Heritage',
        subtitle: 'Historical Capital of India'
      },
      international: {
        title: 'International Heritage',
        subtitle: 'Global Historical Connections'
      },
      allSites: 'All Sites',
      forts: 'Forts',
      temples: 'Temples',
      monuments: 'Monuments',
      statistics: 'Statistics',
      totalSites: 'Total Sites',
      featuredSites: 'Featured Sites',
      averageRating: 'Average Rating',
      lastUpdated: 'Last Updated',
      exploreMore: 'Explore More',
      viewOnMap: 'View on Map',
      planVisit: 'Plan Visit'
    },

    // Thematic Routes Page
    routes: {
      title: 'Thematic Heritage Routes',
      subtitle: 'Curated journeys through India\'s historical legacy',
      duration: 'Duration',
      difficulty: 'Difficulty',
      bestTime: 'Best Time',
      sites: 'Sites',
      highlights: 'Highlights',
      itinerary: 'Itinerary',
      travelTips: 'Travel Tips',
      routeInfo: 'Route Information',
      planning: 'Planning Your Journey',
      day: 'Day',
      included: 'Included',
      notIncluded: 'Not Included',
      bookNow: 'Book Now',
      downloadGuide: 'Download Guide',
      shareRoute: 'Share Route'
    },

    // Temple Detail Page
    templeDetail: {
      notFound: 'Temple Not Found',
      backToTemples: 'Back to All Temples',
      about: 'About This Temple',
      keyInformation: 'Key Information',
      visitingHours: 'Visiting Hours',
      deity: 'Deity',
      architecture: 'Architecture',
      builtIn: 'Built In',
      location: 'Location',
      bestTimeToVisit: 'Best Time to Visit',
      specialPoojas: 'Special Poojas',
      festivals: 'Festivals',
      dressCode: 'Dress Code',
      photography: 'Photography',
      offerings: 'Offerings',
      quickInfo: 'Quick Info',
      share: 'Share',
      virtualTour: 'Virtual Tour',
      audioGuide: 'Audio Guide',
      directions: 'Directions',
      nearbyPlaces: 'Nearby Places',
      reviews: 'Reviews',
      writeReview: 'Write Review',
      planVisit: 'Plan Visit'
    },

    practicalInfo: {
      title: 'How to Reach & Practical Info',
      subtitle: 'Everything you need before the trip — distances, timings, difficulty, and access notes.',
      howToReach: 'How to reach',
      nearestTown: 'Nearest town / village',
      nearestRailway: 'Nearest railway station',
      nearestBus: 'Nearest bus stand / stop',
      approxTravelTime: 'Approx. travel time',
      fromPune: 'From Pune',
      fromMumbai: 'From Mumbai',
      fromNashik: 'From Nashik',
      roadDirections: 'Road directions',
      beforeYouGo: 'Before you go',
      bestMonths: 'Best months to visit',
      difficulty: 'Difficulty',
      steps: 'Steps / terrain',
      kidFriendly: 'Kid-friendly',
      kidFriendlyYes: 'Yes — generally suitable for families',
      kidFriendlyNo: 'Challenging — not ideal for very young children',
      wheelchair: 'Wheelchair / accessibility',
      entryFee: 'Entry fee',
      timings: 'Visiting hours',
      verifyLocally: 'Not confirmed — verify locally before you travel',
      loading: 'Loading practical info…',
      loadError: 'Could not load practical information',
    },

    visitPlanner: {
      title: 'Visit Planner',
      subtitle: 'Save day trips to your account — share with friends, family, or your trek group.',
      signInRequired: 'Please sign in to plan and save your visits',
      create: 'Create itinerary',
      createTitle: 'New one-day itinerary',
      savePlan: 'Save plan',
      empty: 'No saved plans yet. Create your first day trip!',
      loadError: 'Could not load your saved plans',
      fortsError: 'Failed to load forts',
      saveError: 'Could not save plan',
      shareError: 'Could not create share link',
      deleteError: 'Could not delete plan',
      deleteConfirm: 'Delete "{{name}}"?',
      share: 'Share link',
      linkCopied: 'Link copied!',
      delete: 'Delete',
      print: 'Print / PDF',
      notes: 'Notes',
      nameLabel: 'Trip name',
      namePlaceholder: 'e.g. Weekend fort tour',
      dateLabel: 'Date',
      startLabel: 'Start time',
      endLabel: 'End time',
      notesLabel: 'Notes (optional)',
      notesPlaceholder: 'Packing list, meet-up point, who is coming…',
      selectForts: 'Select forts (in visit order)',
      sharedTitle: 'Shared trip plan',
      sharedNotFound: 'This shared plan was not found or is no longer available',
      backToPlanner: 'Back to planner',
      createYourOwn: 'Create your own plan',
      plannedBy: 'Planned by {{name}}',
      printHeader: 'One-day heritage itinerary',
      printFooter: 'Plan your Maharashtra heritage trips at',
    },

    wadaDetail: {
      notFound: 'Wada Not Found',
      notFoundHint: 'This wada may have been removed or the link is incorrect.',
      backToWadas: 'Back to All Wadas',
      heritageBadge: 'Maharashtra Heritage • Wada',
      about: 'About',
      keyInformation: 'Key Information',
      location: 'Location',
      era: 'Era / period',
      bestTime: 'Best time to visit',
      district: 'District',
      onMap: 'On the map',
      keyFeatures: 'Key features',
      quickInfo: 'Quick info',
    },

    // Common UI Elements
    ui: {
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      cancel: 'Cancel',
      confirm: 'Confirm',
      save: 'Save',
      edit: 'Edit',
      delete: 'Delete',
      close: 'Close',
      next: 'Next',
      previous: 'Previous',
      submit: 'Submit',
      reset: 'Reset',
      search: 'Search',
      filter: 'Filter',
      sort: 'Sort',
      view: 'View',
      share: 'Share',
      download: 'Download',
      upload: 'Upload',
      login: 'Login',
      logout: 'Logout',
      signup: 'Sign Up',
      profile: 'Profile',
      settings: 'Settings',
      help: 'Help',
      about: 'About',
      contact: 'Contact',
      privacy: 'Privacy',
      terms: 'Terms',
      cookie: 'Cookie Policy',
      language: 'Language',
      theme: 'Theme',
      darkMode: 'Dark Mode',
      lightMode: 'Light Mode',
      notifications: 'Notifications',
      dashboard: 'Dashboard',
      home: 'Home',
      back: 'Back',
      menu: 'Menu',
      more: 'More',
      less: 'Less',
      showAll: 'Show All',
      hideAll: 'Hide All',
      selectAll: 'Select All',
      deselectAll: 'Deselect All',
      clear: 'Clear',
      apply: 'Apply',
      refresh: 'Refresh',
      update: 'Update',
      create: 'Create',
      new: 'New',
      add: 'Add',
      remove: 'Remove',
      routes: 'Routes',
      close: 'Close'
    }
  },

  mr: {
    // Navigation
    nav: {
      home: 'मुख्यपृष्ठ',
      brandName: 'गडकोट',
      forts: 'गड',
      allForts: 'सर्व गड',
      audioForts: 'ऑडिओ गड',
      temples: 'मंदिरे',
      blog: 'ब्लॉग',
      wades: 'वाडे',
      language: 'भाषा',
      english: 'English',
      marathi: 'मराठी',
      geographicExplorer: 'भौगोलिक शोध',
      thematicRoutes: 'विषयक मार्ग',
    },

    // Home Page
    home: {
      welcome: 'गडकोट मध्ये आपले स्वागत आहे',
      subtitle: 'महाराष्ट्राच्या भव्य गडांच्या आणि पवित्र मंदिरांच्या माध्यमातून समृद्ध वारशाचा शोध घ्या',
      exploreForts: 'गड शोधा',
      audioTours: 'ऑडिओ टूर',
      visitTemples: 'मंदिरे भेट द्या',
      whyChoose: 'गडकोट का निवडावे?',
      historicalPlaces: 'ऐतिहासिक स्थळे',
      historicalPlacesDesc: 'महाराष्ट्राच्या ऐतिहासिक गडांच्या आणि मंदिरांच्या माध्यमातून शतकांचा इतिहास शोधा',
      curatedInfo: 'निवडक माहिती',
      curatedInfoDesc: 'प्रत्येक स्थळाबद्दल तपशील माहिती, इतिहास, वैशिष्ट्ये आणि भेट देण्यासाठी सर्वोत्तम वेळ',
      travelPlanning: 'प्रवास नियोजन',
      travelPlanningDesc: 'आमच्या सविस्तर मार्गदर्शकांसह आणि हंगामी शिफारशींसह आपले भेटी नियोजित करा',
      startExploring: 'शोध सुरू करा',
      historicalForts: 'ऐतिहासिक गड',
      historicalFortsDesc: 'महाराष्ट्राच्या इतिहासाचे रूप बदलणारे भव्य गड शोधा',
      exploreFortsAction: 'गड शोधा',
      audioToursDesc: 'इमर्सिव्ह ऑडिओ मार्गदर्शकांसह आणि वर्णनांसह गडांचा अनुभव घ्या',
      startAudioTour: 'ऑडिओ टूर सुरू करा',
      sacredTemples: 'पवित्र मंदिरे',
      sacredTemplesDesc: 'आध्यात्मिक वारसाचे प्रदर्शन करणारी दिव्य मंदिरे भेट द्या',
      visitTemplesAction: 'मंदिरे भेट द्या',
      footerTitle: 'गडकोट',
      footerDesc: 'महाराष्ट्राचा वारसा जपणे आणि प्रदर्शित करणे',
      exploreHeritage: 'वारसा अन्वेषण',
      exploreHeritageSubtitle: 'आमच्या निवडक संग्रहाद्वारे भारताचा समृद्ध ऐतिहासिक वारसा शोधा',
      geoExplorerDesc: 'विविध प्रदेश आणि विषयांतील ऐतिहासिक ठिकाणे शोधा',
      geoExplorerCta: 'शोधा',
      thematicRoutesDesc: 'इतिहासातील निवडक प्रवास आणि मार्ग',
      thematicRoutesCta: 'आविष्कार करा',
      historicalFortsTitle: 'ऐतिहासिक गड',
      historicalFortsDesc: 'महाराष्ट्राचे भव्य गडकोट आणि दुर्ग अनुभवा',
      historicalFortsCta: 'भेट द्या',
      sacredTemplesTitle: 'पवित्र मंदिरे',
      sacredTemplesDesc: 'आध्यात्मिक वारसाचा अनुभव घ्या',
      sacredTemplesCta: 'दर्शन घ्या',
      featuredFortsTitle: 'वैशिष्ट्यपूर्ण गड',
      featuredFortsSubtitle: 'महाराष्ट्राचे वैभवशाली गडकोट पहा',
      viewAllForts: 'सर्व गड पहा',
      viewFort: 'गड पहा',
      loadingForts: 'गड लोड होत आहेत…',
      noFeaturedForts: 'सध्या गड उपलब्ध नाहीत. लवकरच पुन्हा तपासा.',
      fortLoadError: 'वैशिष्ट्यपूर्ण गड लोड होऊ शकले नाहीत',
      searchSites: 'ठिकाणे शोधा',
      learnMore: 'अधिक जाणून घ्या',
      contactUs: 'संपर्क करा',
      nearbyFortsTitle: 'जवळचे गड',
      nearbyFortsShowing: 'गड दाखवले',
      nearbyFortsEmpty: 'जवळ नकाशा असलेले गड नाहीत — दुसरे शहर निवडा किंवा संपूर्ण नकाशा उघडा.',
      nearbyFortsCta: 'सर्व जवळचे गड पहा',
      nearbyFortsDesc: 'तुमच्या स्थानापासून खरी अंतरे — जवळचे गड शोधा',
      nearbyFortsCtaShort: 'जवळ शोधा',
    },

    // Forts Page
    forts: {
      title: 'ऐतिहासिक गड',
      subtitle: 'महाराष्ट्राच्या भव्य गडांचा शोध घ्या',
      built: 'बांधले',
      height: 'उंची',
      keyFeatures: 'मुख्य वैशिष्ट्ये',
      bestTime: 'सर्वोत्तम वेळ',
      audioGuideAvailable: 'ऑडिओ मार्गदर्शक उपलब्ध'
    },

    // All Forts Page
    allForts: {
      title: 'सर्व गड',
      subtitle: 'महाराष्ट्राच्या गडांचा संपूर्ण संग्रह शोधा',
      fortsFound: 'गड सापडले',
      searchPlaceholder: 'नाव, स्थान किंवा वर्णनानुसार गड शोधा...',
      allLocations: 'सर्व स्थाने',
      sortByName: 'नावानुसार क्रमवारी',
      sortByLocation: 'स्थानानुसार क्रमवारी',
      sortByHeight: 'उंचीनुसार क्रमवारी',
      sortByYear: 'वर्षानुसार क्रमवारी',
      sortByDifficulty: 'कठिणता नुसार क्रमवारी',
      sortByPopularity: 'लोकप्रियता नुसार क्रमवारी',
      noFortsFound: 'कोणतेही गड सापडले नाहीत',
      tryDifferentSearch: 'वेगळा शोध शब्द किंवा फिल्टर वापरून पहा',
      viewDetails: 'तपशील पहा',
      advancedFilters: 'प्रगत फिल्टर',
      clearFilters: 'फिल्टर साफ करा',
      difficulty: 'कठिणता पातळी',
      allDifficulties: 'सर्व कठिणता',
      fortType: 'किल्ल्याचा प्रकार',
      allTypes: 'सर्व प्रकार',
      region: 'प्रदेश',
      allRegions: 'सर्व प्रदेश',
      heightRange: 'उंचीची श्रेणी',
      yearRange: 'वर्षाची श्रेणी',
      gridView: 'ग्रिड दृश्य',
      mapView: 'नकाशा दृश्य',
      trendingForts: 'लोकप्रिय गड',
      recentlyVisited: 'अलीकडी भेट दिलेले',
      visits: 'भेटी',
      nearbyForts: 'जवळचे गड',
      mapListTitle: 'नकाशावरील गड',
      mapHint: 'चिन्ह किंवा यादीतील नावावर टॅप करून गड नकाशावर पहा. ट्रेक आणि इतिहासासाठी तपशील पहा.'
    },

    nearbyForts: {
      title: 'जवळचे गड',
      heritageBadge: 'सह्याद्री • वारसा नकाशा',
      subtitle: '“मी {{place}} येथे आहे — जवळ काय आहे?” — तुमच्या स्थानापासून खरी अंतरे.',
      subtitlePending: 'स्थान परवानगी द्या किंवा शहर निवडा — जवळचे गड शोधा.',
      mapTitle: 'वारसा नकाशा — जवळचे गड',
      mapLegend: 'नकाशीय सर्वे · त्रिज्येतील गड',
      mapFootnote: 'अंतरे Gadkot संग्रहातील गड निर्देशांकावरून — थेट रेषीय अंदाज.',
      fortMarker: 'गड',
      useMyLocation: 'माझे स्थान वापरा',
      myLocation: 'माझे सध्याचे स्थान',
      locationPromptTitle: 'जवळचे गड दाखवण्यासाठी स्थान हवे',
      locationPromptBody: 'तुमच्या फोन किंवा ब्राउझरचे GPS स्थान वापरून आम्ही जवळच्या गडांपासून खरी अंतरे (किमी) दाखवतो. स्थान फक्त या यादीसाठी वापरले जाते — जतन किंवा शेअर होत नाही.',
      locationPromptHint: 'खालील “माझे स्थान वापरा” बटण दाबा — ब्राउझर परवानगी विचारेल.',
      locationRequesting: 'स्थान परवानगीची वाट पाहत आहे…',
      youAreHere: 'तुम्ही येथे आहात',
      placePlaceholder: 'गाव/शहर शोधा — उदा. लोणावळा, पुणे, नाशिक…',
      searchPlace: 'ठिकाण शोधा',
      placeNotFound: 'ठिकाण सापडले नाही — वरच्या बटणांपैकी एक निवडा.',
      geoUnsupported: 'या ब्राउझरमध्ये स्थान समर्थित नाही.',
      geoDenied: 'GPS मिळाला नाही — खाली शहर निवडा किंवा शोधा.',
      detecting: 'स्थान शोधत आहे…',
      loadingForts: 'जवळचे गड लोड होत आहेत…',
      loadError: 'जवळचे गड लोड करता आले नाहीत',
      fortsFound: 'गड जवळ',
      radius: 'त्रिज्या',
      category: 'प्रकार',
      allTypes: 'सर्व प्रकार',
      hillFort: 'गिरीदुर्ग',
      seaFort: 'समुद्र दुर्ग',
      coastalFort: 'किनारी दुर्ग',
      sortBy: 'क्रम',
      sortDistance: 'सर्वात जवळ',
      sortName: 'नाव',
      sortDifficulty: 'अडचण',
      directions: 'दिशा',
      viewDetails: 'तपशील पहा',
      noneFound: 'या त्रिज्येत गड नाहीत',
      noneFoundHint: '{{radius}} km मध्ये नकाशा असलेले गड नाहीत. त्रिज्या वाढवा किंवा दुसरे शहर निवडा.',
      increaseRadius: 'अधिक दूर शोधा',
      footerNote: 'अंतरे Gadkot गड निर्देशांकावरून — ट्रेक/प्रवेश स्थानिक पातळीवर तपासा.',
      places: {
        lonavala: 'लोणावळा',
        pune: 'पुणे',
        mumbai: 'मुंबई',
        nashik: 'नाशिक',
        mahabaleshwar: 'महाबळेश्वर',
        kolhapur: 'कोल्हापूर',
        satara: 'सातारा',
        aurangabad: 'औरंगाबाद',
      },
    },

    // Fort Detail Page
    fortDetail: {
      notFound: 'गड सापडला नाही',
      backToForts: 'सर्व गडांकडे परत जा',
      heritageBadge: 'सह्याद्री • गड • वारसा',
      about: 'या गडांबद्दल',
      keyInformation: 'मुख्य माहिती',
      visitingHours: 'भेट देण्याची वेळ',
      audioGuideAvailable: 'ऑडिओ मार्गदर्शक उपलब्ध',
      audioGuideDescription: 'या गडांच्या रोमांचक इतिहासाचे आणि गोष्टींचे ऐकणे',
      startAudioGuide: 'ऑडिओ मार्गदर्शक सुरू करा',
      quickInfo: 'चांगली माहिती',
      location: 'स्थान',
      builtIn: 'बांधले',
      elevation: 'उंची',
      bestTimeToVisit: 'भेट देण्याचा सर्वोत्तम वेळ',
      audioGuide: 'ऑडिओ मार्गदर्शक',
      addFavorite: 'आवडीत जोडा',
      removeFavorite: 'आवडीतून काढा',
      share: 'शेअर करा',
      historicalTimeline: 'ऐतिहासिक कालरेखा',
      storiesLegends: 'कथा आणि दंतकथा',
      photographyTips: 'फोटोग्राफी टिप्स',
      photographyTip1: 'सूर्योदय/सूर्यास्ताची वेळ सर्वोत्तम प्रकाश देते',
      photographyTip2: 'वाइड-एंगल लेन्स किल्ल्याचे भव्यपणा टिकवून ठेवतात',
      photographyTip3: 'वरून मिळणारे अद्भुत दृश्य गहाळ करू नका',
      audioGuidesTitle: 'बहुभाषिक ऑडिओ मार्गदर्शक',
      richMedia: 'मिडिया अनुभव',
      virtualTour: 'आभासी सैर',
      videoDocumentary: 'व्हिडिओ माहितीपट',
      architectureDetails: 'वास्तुकला तपशील',
      navLocationFeatures: 'नेव्हिगेशन आणि स्थान वैशिष्ट्ये',
      gpsNavigation: 'GPS मार्गदर्शन',
      arView: 'AR दृश्य',
      offlineMaps: 'ऑफलाइन नकाशे',
      nearbyPlaces: 'जवळची ठिकाणे',
      signInForFavorites: 'आवडीत जोडण्यासाठी कृपया साइन इन करा.',
      noKeyFeatures: 'या गडासाठी अद्याप मुख्य वैशिष्ट्ये नोंदवलेली नाहीत. प्रवेश माहितीसाठी वरील वर्णन पहा.',
      multimediaExperiences: 'मल्टिमिडिया अनुभव',
      audioGuides: 'ऑडिओ मार्गदर्शक',
      qrScanner: 'QR स्कॅनर',
      qrScannerTitle: 'QR कोड स्कॅनर',
      virtualTour360Short: '३६०° सैर',
      reenactments: 'ऐतिहासिक पुनर्निर्मिती',
      communitySocialFeatures: 'समुदाय आणि सामाजिक वैशिष्ट्ये',
      photoCommunity: 'फोटो समुदाय',
      reviews: 'पुनरावलोकने',
      reviewsTitle: 'पुनरावलोकने आणि गुण',
      checkIn: 'चेक-इन',
      checkInTitle: 'चेक-इन प्रणाली',
      socialShare: 'सामाजिक शेअर',
      socialShareTitle: 'सामाजिक समाकलन',
      myProfile: 'माझे प्रोफाइल',
    },

    multilingualAudioGuide: {
      transcript: 'प्रतिलेख',
      selectLanguage: 'भाषा निवडा',
      langEnglish: 'इंग्रजी',
      langMarathi: 'मराठी',
      langHindi: 'हिंदी',
      minutes: '{{count}} मिनिटे',
      chapterMin: '{{count}} मिनि',
      online: 'ऑनलाइन',
      offline: 'ऑफलाइन',
      ttsHint: 'वर्णनासाठी आपल्या उपकरणाचा मजकूर-ते-भाषण वापरला जातो (MP3 फाईल आवश्यक नाही).',
      chapters: 'प्रकरणे',
      speed: 'वेग:',
      downloading: 'डाउनलोड होत आहे... {{progress}}%',
      saveTranscript: 'प्रतिलेख जतन करा ({{size}})',
      shareGuide: 'मार्गदर्शक शेअर करा',
      moreFeatures: 'अधिक वैशिष्ट्ये',
      historicalContext: 'ऐतिहासिक संदर्भ',
      historicalContextDesc: 'ऐतिहासिक महत्त्व आणि घटनांच्या कालरेखेचा सखोल अभ्यास',
      expertCommentary: 'तज्ज्ञ टिप्पणी',
      expertCommentaryDesc: 'इतिहासकार आणि पुरातत्त्ववेत्त्यांच्या अतिरिक्त माहिती',
      locationStories: 'स्थान-आधारित कथा',
      locationStoriesDesc: 'गडातील वेगवेगळ्या भागांशी संबंधित कथा',
      interactiveQuiz: 'संवादात्मक प्रश्नमंजुषा',
      interactiveQuizDesc: 'गडांबद्दलच्या प्रश्नांनी आपले ज्ञान तपासा',
      errors: {
        noContent: 'या प्रकरणासाठी मार्गदर्शक सामग्री उपलब्ध नाही.',
        playFailed: 'ऑडिओ मार्गदर्शक चालवता आला नाही. दुसरी भाषा किंवा ब्राउझर वापरून पहा.',
        ttsUnsupported: 'या ब्राउझरमध्ये मजकूर-ते-भाषण समर्थित नाही.'
      }
    },

    timeline: {
      close: 'बंद करा',
      selectEvent: 'तपशील पाहण्यासाठी घटनेवर क्लिक करा',
      year: 'वर्ष',
      impact: 'परिणाम',
      moreDetails: 'अधिक माहिती',
      eraOf: 'युग',
      eventsCount: 'घटना',
      navPrev: 'मागील युग',
      navNext: 'पुढील युग',
      defaultSubtitle: 'मराठा साम्राज्याच्या उंची-नीचींचा प्रवास',
      raigadSubtitle: 'छत्रपती शिवाजी महाराजांच्या राजधानीचा प्रवास',
      rangeDefault: '१६०० – १८१८',
      rangeRaigad: '१६४७ – आज',
    },

    gpsNavigation: {
      navigateTo: '{{name}} पर्यंत नेव्हिगेट करा',
      distance: 'अंतर',
      duration: 'कालावधी',
      steps: 'पायऱ्या',
      yourLocation: 'आपले स्थान',
      accuracy: 'अचूकता',
      viewDetails: 'तपशील पहा',
      toggleNearby: 'जवळची ठिकाणे दाखवा',
      recalculateRoute: 'मार्ग पुन्हा मोजा',
      nearbyPlaces: 'जवळची ठिकाणे',
      startNavigation: 'नेव्हिगेशन सुरू करा',
      stopNavigation: 'नेव्हिगेशन थांबवा',
      trackingActive: 'ट्रॅकिंग सक्रिय',
      continueStraight: 'सरळ पुढे जा',
      followRoute: 'मार्गाचे अनुसरण करा',
      locationError: 'स्थान त्रुटी',
      retry: 'पुन्हा प्रयत्न करा',
      close: 'बंद करा',
      loading: 'नकाशा लोड होत आहे…',
      placeTypes: {
        restaurant: 'रेस्टॉरंट',
        hotel: 'हॉटेल',
        parking: 'पार्किंग',
      },
    },

    arView: {
      experienceTitle: 'AR ऐतिहासिक अनुभव',
      experienceHint: 'किल्ल्यावर कॅमेरा लक्ष्य करा — ऐतिहासिक अधिरचना आणि पुनर्बांधकाम पहा',
      startExperience: 'AR अनुभव सुरू करा',
      stopExperience: 'AR अनुभव थांबवा',
      webxrFallback: 'टीप: WebXR समर्थित नाही. कॅमेरा ओव्हरले मोड वापरला जात आहे.',
      cameraError: 'कॅमेरा वापरता आला नाही. परवानग्या तपासा.',
      howToUseTitle: 'AR दृश्य कसे वापरावे',
      howToUse1: 'कॅमेरा किल्ल्याकडे किंवा ठिकाणाकडे लक्ष्य करा',
      howToUse2: 'वेगवेगळ्या कोनांसाठी उपकरण हलवा',
      howToUse3: 'ऐतिहासिक कालखंड बदलण्यासाठी बाण बटणे वापरा',
      howToUse4: 'ओव्हरले आपल्या उपकरणाच्या दिशेनुसार समायोजित होते',
      howToUse5: 'सर्वोत्तम निकालासाठी किल्ल्यापासून योग्य अंतरावर उभे राहा',
      overlayCounter: '{{current}} / {{total}}',
      overlayOriginalTitle: 'मूळ रचना',
      overlayOriginalDesc: '{{year}} मध्ये बांधलेला {{name}} कसा दिसत असेल ते पहा',
      overlayStrategicTitle: 'सामरिक दृश्य',
      overlayStrategicYear: 'ऐतिहासिक काल',
      overlayStrategicDesc: 'किल्ल्याचे सामरिक महत्त्व आणि लष्करी मांडणी पहा',
      overlayBattleTitle: 'युद्ध दृश्य',
      overlayBattleYear: 'ऐतिहासिक लढाई',
      overlayBattleDesc: 'येथे घडलेल्या महत्त्वाच्या ऐतिहासिक लढायांची कल्पना करा',
      prevOverlay: 'मागील ओव्हरले',
      nextOverlay: 'पुढील ओव्हरले',
    },

    offlineMaps: {
      subtitle: 'ऑफलाइन वापरासाठी नकाशे डाउनलोड करा',
      onlineReady: 'ऑनलाइन — डाउनलोडसाठी तयार',
      offlineUsing: 'ऑफलाइन — डाउनलोड केलेले नकाशे वापरत आहे',
      storageUsage: 'साठवण: {{used}}MB / {{total}}MB वापरले',
      storageWarning: 'साठवण जागा कमी होत आहे. काही नकाशे काढून टाका.',
      downloadedMaps: 'डाउनलोड केलेले नकाशे ({{count}})',
      clearAll: 'सर्व साफ करा',
      clearAllConfirm: 'सर्व ऑफलाइन नकाशे हटवायचे आहेत का?',
      mapMeta: '{{size}}MB • {{date}} रोजी डाउनलोड',
      totalDownloaded: 'एकूण डाउनलोड: {{size}}MB',
      availableMaps: 'उपलब्ध नकाशे',
      estimatedSize: 'अंदाजे आकार: ~१५MB',
      downloaded: 'डाउनलोड झाले',
      download: 'डाउनलोड',
      downloadFailed: 'नकाशा डाउनलोड होऊ शकला नाही. पुन्हा प्रयत्न करा.',
      needInternet: 'नकाशे डाउनलोड करण्यासाठी इंटरनेट आवश्यक आहे',
      tipsTitle: 'ऑफलाइन नकाशांसाठी टिप्स',
      tips1: 'स्थिर इंटरनेट असताना नकाशे डाउनलोड करा',
      tips2: 'इंटरनेट नसलेल्या भागात भेट देण्याची योजना असल्यास नकाशे आधीच डाउनलोड करा',
      tips3: 'नकाश्यात नेव्हिगेशन आणि जवळच्या ठिकाणांची माहिती समाविष्ट आहे',
      tips4: 'नवीनतम माहितीसाठी नकाशे नियमित अपडेट करा',
    },

    nearbyAttractions: {
      aroundFort: '{{name}} जवळ',
      searchPlaceholder: 'जवळची ठिकाणे शोधा…',
      placesFound: '{{count}} ठिकाणे सापडली',
      sortDistance: 'अंतरानुसार',
      sortName: 'नावानुसार',
      sortRating: 'गुणानुसार',
      noPlacesFound: 'जवळची ठिकाणे सापडली नाहीत',
      loading: 'जवळची ठिकाणे लोड होत आहेत…',
      directions: 'दिशा',
      call: 'कॉल',
      away: '{{distance}} अंतरावर',
      cuisine: 'पाककृती',
      hours: 'वेळ',
      website: 'वेबसाइट',
      visitWebsite: 'वेबसाइट पहा',
      showMap: 'नकाशा दाखवा',
      hideMap: 'नकाशा लपवा',
      getDirections: 'दिशा मिळवा',
      rating: '{{rating}} गुण',
      categories: {
        all: 'सर्व ठिकाणे',
        restaurant: 'रेस्टॉरंट',
        hotel: 'हॉटेल',
        parking: 'पार्किंग',
        cafe: 'कॅफे',
        shopping: 'खरेदी',
        fuel: 'पेट्रोल पंप',
        attraction: 'आकर्षणे',
      },
    },

    qrScanner: {
      history: 'इतिहास',
      scanHistory: 'स्कॅन इतिहास',
      startScan: 'स्कॅन सुरू करा',
      readyToScan: 'स्कॅनसाठी तयार',
      stopScan: 'थांबवा',
      pointCamera: 'किल्ल्यावरील QR कोडवर कॅमेरा लक्ष्य करा',
      idleDescription: 'किल्ल्यावरील QR कोड स्कॅन करून त्वरित माहिती, कथा आणि मल्टिमिडिया मिळवा',
      scanning: 'स्कॅन होत आहे…',
      scanSuccess: 'स्कॅन यशस्वी',
      scanFailed: 'QR कोड वाचता आला नाही. पुन्हा प्रयत्न करा.',
      noHistory: 'अद्याप स्कॅन इतिहास नाही',
      noScansYet: 'अद्याप स्कॅन नाही. इतिहास पाहण्यासाठी QR कोड स्कॅन करा.',
      offline: 'ऑफलाइन',
      online: 'ऑनलाइन',
      signInRequired: 'स्कॅन जतन करण्यासाठी साइन इन करा',
      whatYouCanScan: 'आपण काय स्कॅन करू शकता',
      howToUse: 'कसे वापरावे',
      featureHistoricalTitle: 'ऐतिहासिक माहिती',
      featureHistoricalDesc: 'रचनांचा सविस्तर इतिहास आणि महत्त्व',
      featureNavigationTitle: 'नेव्हिगेशन मार्गदर्शक',
      featureNavigationDesc: 'किल्ल्यातील दिशा आणि मार्ग',
      featureStoriesTitle: 'कथा आणि दंतकथा',
      featureStoriesDesc: 'किल्ल्याशी संबंधित आख्यायिका आणि गोष्टी',
      featureAudioTitle: 'ऑडिओ मार्गदर्शक',
      featureAudioDesc: 'अनेक भाषांमध्ये वर्णन केलेले सैर',
      step1: 'किल्ल्यातील वेगवेगळ्या ठिकाणी QR कोड प्लेट शोधा',
      step2: '"स्कॅन सुरू करा" वर टॅप करा आणि कॅमेरा QR कोडवर लक्ष्य करा',
      step3: 'स्कॅन पूर्ण होईपर्यंत थांबा आणि माहितीचा आनंद घ्या',
      step4: 'माहिती शेअर करा, बुकमार्क करा किंवा ऑफलाइनसाठी डाउनलोड करा',
      audioGuideAvailable: 'ऑडिओ मार्गदर्शक उपलब्ध',
      videoTourAvailable: 'व्हिडिओ सैर उपलब्ध',
      share: 'शेअर करा',
      download: 'डाउनलोड',
      cameraGranted: 'कॅमेरा परवानगी आहे',
      cameraDenied: 'कॅमेरा नाकारले',
      cameraPrompt: 'कॅमेरा परवानगी आवश्यक',
      categories: {
        historical: 'ऐतिहासिक',
        navigation: 'नेव्हिगेशन',
        legend: 'दंतकथा',
        architecture: 'वास्तुकला',
        information: 'माहिती',
      },
    },

    virtualTour360: {
      scenes: 'दृश्ये',
      dragToExplore: 'पॅनोरमा पाहण्यासाठी ड्रॅग करा',
      sceneInfo: 'दृश्य माहिती',
      mapView: 'नकाशा दृश्य',
    },

    historicalReenactments: {
      pageTitle: 'ऐतिहासिक पुनर्निर्मिती',
      pageDescription:
        '{{name}} चा समृद्ध इतिहास काळजीपूर्वक तयार केलेल्या ऐतिहासिक पुनर्निर्मितींद्वारे अनुभवा. वेगवेगळ्या युगातील लढाई, सोहळे आणि दैनंदिन जीवनाचे नाट्यमय चित्रण पहा.',
      searchPlaceholder: 'व्हिडिओ, टॅग किंवा वर्णन शोधा…',
      offline: 'ऑफलाइन',
      online: 'ऑनलाइन',
      allVideos: 'सर्व व्हिडिओ',
      battles: 'लढाई',
      dailyLife: 'दैनंदिन जीवन',
      ceremonies: 'सोहळे',
      documentaries: 'माहितीपट',
      noVideos: 'व्हिडिओ सापडले नाहीत',
      watchNow: 'आता पहा',
      relatedVideos: 'संबंधित व्हिडिओ',
      views: '{{count}} पाहिले',
      tags: 'टॅग',
      categories: {
        battle: 'लढाई',
        lifestyle: 'दैनंदिन जीवन',
        ceremony: 'सोहळा',
        documentary: 'माहितीपट',
      },
    },

    photoCommunity: {
      upload: 'अपलोड',
      searchPlaceholder: 'फोटो, टॅग किंवा वापरकर्ते शोधा…',
      sortRecent: 'अलीकडील',
      sortPopular: 'लोकप्रिय',
      sortViews: 'सर्वाधिक पाहिले',
      sortComments: 'सर्वाधिक टिप्पण्या',
      filterAll: 'सर्व',
      filterFeatured: 'वैशिष्ट्यीकृत',
      filterFollowing: 'अनुसरण',
      noPhotos: 'अद्याप फोटो नाहीत',
      beFirst: '{{name}} चा पहिला फोटो शेअर करा!',
      uploadFirst: 'पहिला फोटो अपलोड करा',
      signInUpload: 'फोटो अपलोड करण्यासाठी साइन इन करा',
      likes: '{{count}} आवडी',
      comments: '{{count}} टिप्पण्या',
      loadError: 'फोटो लोड होऊ शकले नाहीत. पुन्हा प्रयत्न करा.',
      retry: 'पुन्हा प्रयत्न करा',
      loading: 'फोटो लोड होत आहेत…',
      followingUnavailable: 'अनुसरण फीड अद्याप उपलब्ध नाही.',
      featuredBadge: 'वैशिष्ट्यीकृत',
      uploadModalTitle: 'फोटो अपलोड करा',
      captionPlaceholder: 'मथळा जोडा (पर्यायी)…',
      shareAtFort: '{{name}} येथील आपला अनुभव शेअर करा',
      choosePhoto: 'फोटो निवडा',
      fileHint: 'JPG किंवा PNG, १०MB पर्यंत',
      uploading: 'अपलोड होत आहे…',
      uploadSuccess: 'फोटो सबमिट झाला! मंजुरीनंतर दिसेल.',
      uploadFailed: 'फोटो अपलोड होऊ शकला नाही. पुन्हा प्रयत्न करा.',
      today: 'आज',
      yesterday: 'काल',
      daysAgo: '{{count}} दिवसांपूर्वी',
      weeksAgo: '{{count}} आठवड्यांपूर्वी',
      linkCopied: 'दुवा क्लिपबोर्डवर कॉपी झाला!',
      anonymousUser: 'पर्यटक',
    },

    reviewsRatings: {
      writeReview: 'पुनरावलोकन लिहा',
      searchPlaceholder: 'पुनरावलोकने शोधा…',
      reviewsCount: '{{count}} पुनरावलोकने',
      sortRecent: 'अलीकडील',
      sortHelpful: 'सर्वाधिक उपयुक्त',
      sortRatingHigh: 'उच्च गुण',
      sortRatingLow: 'कमी गुण',
      filterAll: 'सर्व',
      filterVerified: 'सत्यापित',
      filterWithPhotos: 'फोटोसह',
      helpful: 'उपयुक्त',
      verified: 'सत्यापित भेट',
      signInReview: 'पुनरावलोकन लिहिण्यासाठी साइन इन करा.',
      submitReview: 'पुनरावलोकन सबमिट करा',
      noReviews: 'अद्याप पुनरावलोकने नाहीत',
      noReviewsHint: 'पार्किंग, पायऱ्या, गर्दी, सर्वोत्तम वेळ — कुटुंबांना नियोजनासाठी मदत करा.',
      yourReview: 'आपले पुनरावलोकन',
      editReview: 'पुनरावलोकन संपादित करा',
      ratingLabel: 'गुण',
      titleLabel: 'शीर्षक (ऐच्छिक)',
      commentLabel: 'आपला अनुभव',
      visitDateLabel: 'भेट दिनांक (ऐच्छिक)',
      titlePlaceholder: 'उदा. सोपी चढाई, रविवारी गर्दी',
      commentPlaceholder: 'पार्किंग, पायऱ्या, गर्दी, भेटीसाठी सर्वोत्तम वेळ…',
      confirmDelete: 'आपले पुनरावलोकन कायमचे हटवायचे?',
      loadError: 'पुनरावलोकने लोड होऊ शकली नाहीत.',
      submitError: 'पुनरावलोकन जतन होऊ शकले नाही.',
      deleteError: 'पुनरावलोकन हटवता आले नाही.',
      visitedOn: 'भेट {{date}}',
      today: 'आज',
      yesterday: 'काल',
      daysAgo: '{{count}} दिवसांपूर्वी',
      weeksAgo: '{{count}} आठवड्यांपूर्वी',
    },

    checkInSystem: {
      passport: 'पासपोर्ट',
      totalCheckIns: 'एकूण चेक-इन',
      dayStreak: 'सलग दिवस',
      uniqueForts: 'वेगवेगळे गड',
      achievements: 'यश',
      alreadyCheckedIn: 'आधीच चेक-इन झाले!',
      checkedInToday: 'आज {{name}} येथे आधीच चेक-इन केले आहे.',
      lastCheckIn: 'शेवटचे चेक-इन:',
      readyToCheckIn: 'चेक-इन करण्यास तयार?',
      checkInPrompt: 'हा किल्ला आपल्या पासपोर्टमध्ये जोडा आणि प्रवास मोजा!',
      checkInNow: 'आता चेक-इन करा',
      passportSoon: 'पासपोर्ट डाउनलोड लवकरच उपलब्ध!',
      signInRequired: 'चेक-इन करण्यासाठी साइन इन करा',
    },

    socialIntegration: {
      connect: 'जोडा',
      totalShares: 'एकूण शेअर',
      socialLikes: 'सामाजिक आवडी',
      comments: 'टिप्पण्या',
      mentions: 'उल्लेख',
      connectedAccounts: 'जोडलेले खाते',
      notConnected: 'जोडलेले नाही',
      followers: '{{count}} अनुयायी',
      shareExperience: 'आपला अनुभव शेअर करा',
      copyLink: 'दुवा कॉपी करा',
      linkCopied: 'दुवा कॉपी झाला!',
      shareNow: 'आता शेअर करा',
      connectAccounts: 'खाती जोडा',
    },

    userProfile: {
      overview: 'आढावा',
      achievements: 'यश',
      activity: 'क्रियाकलाप',
      stats: 'आकडेवारी',
      fortsVisited: 'भेट दिलेले गड',
      photosShared: 'शेअर केलेले फोटो',
      reviewsWritten: 'लिहिलेली पुनरावलोकने',
      badgesEarned: 'मिळवलेले बॅज',
      editProfile: 'प्रोफाइल संपादित करा',
      saveProfile: 'जतन करा',
      cancelEdit: 'रद्द करा',
      signInRequired: 'प्रोफाइल पाहण्यासाठी साइन इन करा',
      memberSince: '{{date}} पासून सदस्य',
    },

    fortMedia: {
      close: 'बंद करा',
      chapters: 'प्रकरणे',
      storiesSubtitle: 'ऐतिहासिक कथा',
      storiesCount: 'कथा',
      storyDetails: 'कथेचा तपशील',
      category: 'वर्ग',
      timePeriod: 'कालखंड',
      totalDuration: 'एकूण कालावधी',
      minutes: 'मिनिटे',
      relatedStories: 'संबंधित कथा',
      characters: 'पात्रे',
      moralTitle: 'कथेचा नैतिक',
      previous: 'मागील',
      next: 'पुढील',
      playAudio: 'ऑडिओ चालू करा',
      pauseAudio: 'ऑडिओ थांबवा',
      play: 'चालू',
      pause: 'थांबवा',
      audioNarration: 'ऑडिओ वर्णन',
      architectureTitle: 'वास्तुकला तपशील',
      view3d: '३डी दृश्य',
      viewBlueprint: 'रेखाट',
      viewTechnique: 'बांधकाम पद्धत',
      model3d: '३डी मॉडेल',
      controls3d: '३डी नियंत्रण',
      autoRotate: 'स्वयं फिरवा',
      stopRotation: 'फिरवणे थांबवा',
      keyFeatures: 'मुख्य वैशिष्ट्ये',
      dimensions: 'मोजमाप',
      sections: 'विभाग',
      blueprintView: 'रेखाट दृश्य',
      blueprintHint: 'संवादात्मक रेखाट येथे दिसेल',
      constructionMethod: 'बांधकाम पद्धत',
      materials: 'साहित्य',
      tools: 'साधने',
      timeline: 'कालावधी',
      constructionProcess: 'बांधकाम प्रक्रिया',
      processFoundation: 'पाया खोदणे आणि तयारी',
      processStone: 'दगड कापणे आणि आकार',
      processWall: 'भिंत बांधकाम आणि बळकटी',
      processRoof: 'छत आणि अंतिम सजावट',
      dimWidth: 'रुंदी',
      dimHeight: 'उंची',
      dimDepth: 'खोली',
      dimPerimeter: 'परिघ',
      dimArea: 'क्षेत्रफळ',
    },

    // Audio Forts Page
    audioForts: {
      title: 'ऑडिओ गड',
      subtitle: 'इमर्सिव्ह ऑडिओ मार्गदर्शकांसह महाराष्ट्राच्या गडांचा अनुभव घ्या',
      availableCount: 'ऑडिओ मार्गदर्शक उपलब्ध असलेले गड',
      noAudioGuides: 'ऑडिओ मार्गदर्शक उपलब्ध नाही',
      noAudioGuidesDesc: 'सध्या ऑडिओ मार्गदर्शकांसह कोणतेही गड नाहीत. लवकरच परत तपासा!',
      audioGuideSections: 'ऑडिओ मार्गदर्शक विभाग',
      startAudioTour: 'ऑडिओ टूर सुरू करा'
    },

    // Temples Page
    temples: {
      title: 'पवित्र मंदिरे',
      subtitle: 'महाराष्ट्राचा आध्यात्मिक वारसा शोधा',
      deity: 'देवता',
      keyFeatures: 'मुख्य वैशिष्ट्ये',
      bestTime: 'सर्वोत्तम वेळ'
    },

    // Audio Guide
    audioGuide: {
      title: 'ऑडिओ मार्गदर्शक',
      subtitle: 'स्मार्ट डिजिटल वारसा ऑडिओ अनुभव',
      poweredBy: 'गडकोट द्वारे विकसित',
      sections: {
        introduction: 'परिचय',
        history: 'इतिहास',
        architecture: 'वास्तुकला',
        significance: 'महत्त्व',
        exploration: 'शोध',
        conclusion: 'निष्कर्ष'
      },
      copyright: '© गडकोट — महाराष्ट्र वारसा मंच'
    },

    // Common
    common: {
      meters: 'मीटर',
      yearBuilt: 'बांधले',
      location: 'स्थान',
      description: 'वर्णन',
      features: 'वैशिष्ट्ये'
    },

    // Footer
    footer: {
      title: 'गडकोट',
      description: 'महाराष्ट्राचा वारसा जपणे आणि प्रदर्शित करणे',
      contact: 'आमच्याशी संपर्क साधा',
      followUs: 'आमचे अनुसरण करा',
      socialTagline: 'महाराष्ट्राच्या समृद्ध वारसाचा शोध घ्या',
      rights: '@गडकोट',
      allRightsReserved: 'सर्व हक्क सुरक्षित',
      privacyPolicy: 'गोपनीयता धोरण',
      termsOfService: 'सेवा अटी'
    },

    // About Page
    about: {
      title: 'गडकोट बद्दल',
      subtitle: 'भारताच्या ऐतिहासिक वारसाचे डिजिटल द्वार',
      whatIsGadkot: 'गडकोट म्हणजे काय?',
      whatIsGadkotDesc: 'गडकोट हे भारताच्या समृद्ध ऐतिहासिक वारसाचे जतन करणे, प्रदर्शित करणे आणि प्रमोट करण्यासाठी समर्पित एक संपूर्ण डिजिटल वारसा प्लॅटफॉर्म आहे. आम्ही इमर्सिव्ह डिजिटल अनुभवांद्वारे शतकांचा इतिहास तुमच्या बोटांच्या टोकावर आणतो.',
      primaryGoals: 'प्राथमिक ध्येये',
      goal1: 'ऐतिहासिक ज्ञानात प्रवेशाला लोकशाही बनवणे',
      goal2: 'डिजिटल दस्तऐवजीकरणाद्वारे सांस्कृतिक वारसाचे जतन करणे',
      goal3: 'आकर्षक शैक्षणिक अनुभव तयार करणे',
      goal4: 'लोकांना त्यांच्या ऐतिहासिक मूळांशी जोडणे',
      targetAudience: 'लक्षित प्रेक्षक',
      audience1: 'इतिहास उत्साही आणि संशोधक',
      audience2: 'विद्यार्थी आणि शिक्षक',
      audience3: 'प्रवासी आणि पर्यटक',
      audience4: 'सांस्कृतिक वारसा संरक्षक',
      mission: 'आमचे ध्येय',
      missionAndVisionTitle: 'आमचे ध्येय आणि आमचे दृष्टिकोन',
      missionDesc: 'नावीन्यपूर्ण डिजिटल उपायांद्वारे भारताच्या ऐतिहासिक वारसाचे जतन आणि प्रमोट करणे, इतिहासाला सर्वांसाठी सुलभ आणि आकर्षक बनवणे.',
      vision: 'आमचे दृष्टिकोन',
      visionDesc: 'ऐतिहासिक वारसासाठी जगातील सर्वात संपूर्ण डिजिटल प्लॅटफॉर्म बनणे, लाखो लोकांना त्यांच्या सांस्कृतिक मूळांशी जोडणे.',
      values: 'आमचे मूल्ये',
      value1: 'सांस्कृतिक वारसाचे जतन',
      value2: 'शैक्षणिक उत्कृष्टता',
      value3: 'डिजिटल कथन कथनात नावीन्य',
      value4: 'समुदाय सहभाग',
      achievements: 'उपलब्धी',
      achievement1: 'ऐतिहासिक फोकस आणि शैक्षणिक सामग्री',
      achievement2: 'डिजिटल जतन पहल',
      achievement3: 'ध्येय-संचालित दृष्टिकोन',
      achievement4: 'समुदाय निर्मिती प्रयत्न',
      story: 'आमची कथा',
      storyDesc: '२०२४ मध्ये स्थापन झालेले, गडकोट इतिहास आणि तंत्रज्ञानाच्या आकर्षणातून उदयास आले. आम्ही भावी पिढ्यांसाठी भारताच्या भव्य वारसाचे जतन आणि प्रदर्शन करण्याच्या आमच्या मिशनवर एक नवीन प्लॅटफॉर्म म्हणून सुरुवात करत आहोत.',
      joinUs: 'आमच्या प्रवासात सामील व्हा',
      joinUsDesc: 'एकत्र, भारताच्या ऐतिहासिक वारसाच्या समृद्ध विणकरमध्ये जतन करूया आणि साजरा करूया.'
    },

    // Contact Page
    contact: {
      title: 'आमच्याशी संपर्क साधा',
      subtitle: 'गडकोट टीमशी संपर्क साधा',
      getInTouch: 'संपर्क साधा',
      contactDesc: 'आम्हाला तुमच्याकडून ऐकून आनंद होईल. तुमच्याजवळ प्रश्न, अभिप्राय किंवा सहकार्याच्या संधी असल्यास, फक्त संपर्क साधा.',
      formName: 'नाव',
      formEmail: 'ईमेल',
      formSubject: 'विषय',
      formMessage: 'संदेश',
      formSubmit: 'संदेश पाठवा',
      departments: 'विभाग',
      supportDept: 'समर्थन विभाग',
      supportDesc: 'तांत्रिक सहायता आणि खाते-संबंधित क्वेरींसाठी',
      supportEmail: 'support@gadkot.com',
      contentDept: 'सामग्री विभाग',
      contentDesc: 'सामग्री सूचना आणि ऐतिहासिक माहितीसाठी',
      contentEmail: 'content@gadkot.com',
      partnershipDept: 'भागीदारी विभाग',
      partnershipDesc: 'सहकार्य आणि भागीदारीच्या संधींसाठी',
      partnershipEmail: 'partnerships@gadkot.com',
      officeHours: 'कार्यालयीन वेळ',
      officeDays: 'सोमवार - शुक्रवार: सकाळ ९:०० - संध्याकाळ ६:००',
      weekend: 'शनिवार - रविवार: बंद',
      faq: 'वारंवार विचारले जाणारे प्रश्न',
      faq1: 'मी ऐतिहासिक माहिती कशी योगदान देऊ शकतो?',
      faq1Ans: 'तुम्ही आमच्या सामग्री टीमशी सत्यापित ऐतिहासिक माहिती, फोटो किंवा कागदपत्रांसह संपर्क साधून योगदान देऊ शकता.',
      faq2: 'तुम्ही मार्गदर्शक दौरे ऑफर करता?',
      faq2Ans: 'आम्ही सध्या डिजिटल ऑडिओ मार्गदर्शक ऑफर करतो. भौतिक मार्गदर्शक दौरे स्थानिक अधिकाऱ्यांशी भागीदारीत उपलब्ध असू शकतात.',
      faq3: 'तुमची ऐतिहासिक माहिती किती अचूक आहे?',
      faq3Ans: 'आम्ही सर्व ऐतिहासिक सामग्रीच्या सर्वोच्च अचूकता सुनिश्चित करण्यासाठी इतिहासकार आणि पुरातत्त्व अधिकाऱ्यांसह काम करतो.',
      socialMedia: 'आमच्यासोबत जोडा',
      socialDesc: 'अपडेट्स, ऐतिहासिक तथ्ये आणि समुदाय चर्चांसाठी सोशल मीडियावर आमचे अनुसरण करा.'
    },

    // Blog Page
    blog: {
      title: 'वारसा ब्लॉग',
      subtitle: 'इतिहासप्रेमी आणि प्रवाशांच्या कथा आणि अनुभव',
      heritageBadge: 'मराठी वारसाच्या कथा',
      themeForts: 'गड',
      themeTemples: 'मंदिरे',
      themeHistory: 'इतिहास',
      themeCulture: 'संस्कृती',
      themeTravel: 'प्रवास',
      searchPlaceholder: 'शीर्षक, सामग्री किंवा टॅग्सनुसार ब्लॉग शोधा...',
      categoryFilter: 'सर्व श्रेण्या',
      featuredPost: 'वैशिष्ट्यीकृत पोस्ट',
      latestPosts: 'अलीकडील पोस्ट',
      readMore: 'अधिक वाचा',
      publishedOn: 'प्रकाशित',
      readingTime: 'मिनिटे वाचन',
      authorBy: 'द्वारे',
      addBlog: 'ब्लॉग जोडा',
      blogTitle: 'ब्लॉग शीर्षक',
      blogContent: 'ब्लॉग सामग्री',
      blogCategory: 'श्रेणी',
      blogTags: 'टॅग्स (स्वल्पविरामाने विभाजित)',
      blogImages: 'प्रतिमा (URLs, प्रति ओळ एक)',
      submitBlog: 'ब्लॉग सबमिट करा',
      submitForReview: 'समीक्षेसाठी सबमिट करा',
      reviewNotice: 'तुमचा ब्लॉग २४-४८ तासांच्या आत समीक्षा केला जाईल.',
      categories: {
        forts: 'गड',
        temples: 'मंदिरे',
        historical: 'ऐतिहासिक स्थळे',
        culture: 'संस्कृती',
        travel: 'प्रवास'
      },
      noBlogsFound: 'कोणतेही ब्लॉग सापडले नाहीत',
      tryDifferentSearch: 'वेगळे शोध शब्द किंवा श्रेण्या वापरून पहा',
      views: 'दृश्य',
      likes: 'आवड',
      comments: 'टिप्पण्या'
    },

    // Search Results Page
    search: {
      title: 'शोध परिणाम',
      searchingFor: 'शोधत आहे',
      resultsFound: 'परिणाम सापडले',
      noResults: 'कोणतेही परिणाम सापडले नाहीत',
      noResultsDesc: 'तुमचे शोध शब्द किंवा फिल्टर समायोजित करून पहा',
      filters: 'फिल्टर',
      allTypes: 'सर्व प्रकार',
      fortsType: 'गड',
      templesType: 'मंदिरे',
      routesType: 'मार्ग',
      sortBy: 'नुसार क्रमवारी',
      relevance: 'संबंध',
      name: 'नाव',
      popularity: 'लोकप्रियता',
      recentlyAdded: 'अलीकडील जोडले',
      clearFilters: 'फिल्टर साफ करा',
      searchIn: 'मध्ये शोधा',
      allContent: 'सर्व सामग्री',
      titles: 'शीर्षके',
      descriptions: 'वर्णन',
      tags: 'टॅग्स'
    },

    // Error Pages
    error: {
      notFound: 'पृष्ठ सापडले नाही',
      notFoundDesc: 'तुम्ही शोधत असलेले पृष्ठ अस्तित्वात नाही किंवा हलविण्यात आले आहे.',
      serverError: 'सर्व्हर त्रुटी',
      serverErrorDesc: 'आमच्या बाजूला काहीतरी चूक झाली. कृपया नंतर पुन्हा प्रयत्न करा.',
      forbidden: 'प्रवेश नकारला',
      forbiddenDesc: 'तुम्हाला या पृष्ठात प्रवेश करण्याची परवानगी नाही.',
      serviceUnavailable: 'सेवा उपलब्ध नाही',
      serviceUnavailableDesc: 'आम्ही सध्या देखभालीसाठी डाउन आहोत. कृपया लवकरच तपासा.',
      backToHome: 'होम पृष्ठावर परत जा',
      tryAgain: 'पुन्हा प्रयत्न करा',
      reportIssue: 'समस्या रिपोर्ट करा',
      quickLinks: 'जलद दुवे',
      exploreForts: 'गड शोधा',
      visitTemples: 'मंदिरे भेट द्या',
      readBlog: 'ब्लॉग वाचा',
      contactSupport: 'समर्थनाशी संपर्क साधा'
    },

    // Geographic Detail Page
    geographic: {
      rajasthan: {
        title: 'राजस्थान वारसा',
        subtitle: 'राजांची भूमी आणि भव्य गड'
      },
      delhi: {
        title: 'दिल्ली वारसा',
        subtitle: 'भारताची ऐतिहासिक राजधानी'
      },
      international: {
        title: 'आंतरराष्ट्रीय वारसा',
        subtitle: 'ग्लोबल ऐतिहासिक जोडण्या'
      },
      allSites: 'सर्व स्थळे',
      forts: 'गड',
      temples: 'मंदिरे',
      monuments: 'स्मारक',
      statistics: 'आकडेवारी',
      totalSites: 'एकूण स्थळे',
      featuredSites: 'वैशिष्ट्यीकृत स्थळे',
      averageRating: 'सरासरी रेटिंग',
      lastUpdated: 'शेवटी अपडेट केले',
      exploreMore: 'अधिक शोधा',
      viewOnMap: 'नकाश्यावर पहा',
      planVisit: 'भेट नियोजित करा'
    },

    // Thematic Routes Page
    routes: {
      title: 'विषयवार वारसा मार्ग',
      subtitle: 'भारताच्या ऐतिहासिक वारसातून निवडक सफर',
      duration: 'कालावधी',
      difficulty: 'कठिणता',
      bestTime: 'सर्वोत्तम वेळ',
      sites: 'स्थळे',
      highlights: 'आकर्षण',
      itinerary: 'प्रवास कार्यक्रम',
      travelTips: 'प्रवास टिप्स',
      routeInfo: 'मार्ग माहिती',
      planning: 'तुमची सफर नियोजित करणे',
      day: 'दिवस',
      included: 'समाविष्ट',
      notIncluded: 'समाविष्ट नाही',
      bookNow: 'आता बुक करा',
      downloadGuide: 'मार्गदर्शक डाउनलोड करा',
      shareRoute: 'मार्ग शेअर करा'
    },

    // Temple Detail Page
    templeDetail: {
      notFound: 'मंदिर सापडले नाही',
      backToTemples: 'सर्व मंदिरांकडे परत जा',
      about: 'या मंदिराबद्दल',
      keyInformation: 'मुख्य माहिती',
      visitingHours: 'भेट देण्याची वेळ',
      deity: 'देवता',
      architecture: 'वास्तुकला',
      builtIn: 'बांधले',
      location: 'स्थान',
      bestTimeToVisit: 'भेट देण्याचा सर्वोत्तम वेळ',
      specialPoojas: 'विशेष पूजा',
      festivals: 'उत्सव',
      dressCode: 'पोशाख संहिता',
      photography: 'फोटोग्राफी',
      offerings: 'ऑफरिंग्स',
      quickInfo: 'चांगली माहिती',
      share: 'शेअर करा',
      virtualTour: 'व्हर्चुअल टूर',
      audioGuide: 'ऑडिओ मार्गदर्शक',
      directions: 'दिशा',
      nearbyPlaces: 'जवळची ठिकाणे',
      reviews: 'समीक्षा',
      writeReview: 'समीक्षा लिहा',
      planVisit: 'भेट नियोजित करा'
    },

    practicalInfo: {
      title: 'कसे पोहचाल आणि महत्वाची माहिती',
      subtitle: 'प्रवासापूर्वीची सर्व गोष्टी — अंतर, वेळ, अडचण आणि प्रवेश टिपा.',
      howToReach: 'कसे पोहोचाल',
      nearestTown: 'जवळचे गाव / शहर',
      nearestRailway: 'जवळचे रेल्वे स्थानक',
      nearestBus: 'जवळचे बस स्थानक / थांबा',
      approxTravelTime: 'अंदाजे प्रवास वेळ',
      fromPune: 'पुण्याहून',
      fromMumbai: 'मुंबईहून',
      fromNashik: 'नाशिकहून',
      roadDirections: 'रस्त्याने दिशा',
      beforeYouGo: 'जाण्यापूर्वी',
      bestMonths: 'भेट देण्यासाठी सर्वोत्तम महिने',
      difficulty: 'अडचण / ट्रेक',
      steps: 'पायऱ्या / भूप्रदेश',
      kidFriendly: 'लहान मुलांसाठी',
      kidFriendlyYes: 'होय — सामान्यतः कुटुंबासाठी योग्य',
      kidFriendlyNo: 'अवघड — अतिशय लहान मुलांसाठी अनुचित',
      wheelchair: 'व्हीलचेअर / सुलभ प्रवेश',
      entryFee: 'प्रवेश शुल्क',
      timings: 'भेट देण्याची वेळ',
      verifyLocally: 'निश्चित नाही — प्रवासापूर्वी स्थानिक माहिती घ्या',
      loading: 'प्रॅक्टिकल माहिती लोड होत आहे…',
      loadError: 'प्रॅक्टिकल माहिती लोड करता आली नाही',
    },

    visitPlanner: {
      title: 'भेट नियोजक',
      subtitle: 'एक दिवसाचे प्रवास योजना जतन करा — मित्र, कुटुंब किंवा ट्रेक गटासोबत शेअर करा.',
      signInRequired: 'भेट योजना तयार आणि जतन करण्यासाठी साइन इन करा',
      create: 'योजना तयार करा',
      createTitle: 'नवीन एक-दिवसीय योजना',
      savePlan: 'योजना जतन करा',
      empty: 'अजून जतन केलेली योजना नाही. पहिला दिवसभराचा प्रवास तयार करा!',
      loadError: 'जतन केलेल्या योजना लोड करता आल्या नाहीत',
      fortsError: 'गड लोड करता आले नाहीत',
      saveError: 'योजना जतन करता आली नाही',
      shareError: 'शेअर लिंक तयार करता आली नाही',
      deleteError: 'योजना हटवता आली नाही',
      deleteConfirm: '"{{name}}" हटवायचे?',
      share: 'लिंक शेअर करा',
      linkCopied: 'लिंक कॉपी झाली!',
      delete: 'हटवा',
      print: 'प्रिंट / PDF',
      notes: 'टिपा',
      nameLabel: 'प्रवासाचे नाव',
      namePlaceholder: 'उदा. शनिवार-रविवार गड भ्रमंती',
      dateLabel: 'तारीख',
      startLabel: 'सुरुवातीची वेळ',
      endLabel: 'समाप्तीची वेळ',
      notesLabel: 'टिपा (ऐच्छिक)',
      notesPlaceholder: 'सामान, भेट स्थान, कोण येत आहे…',
      selectForts: 'गड निवडा (भेटीच्या क्रमाने)',
      sharedTitle: 'शेअर केलेली प्रवास योजना',
      sharedNotFound: 'ही शेअर केलेली योजना सापडली नाही किंवा उपलब्ध नाही',
      backToPlanner: 'नियोजकाकडे परत',
      createYourOwn: 'स्वतःची योजना तयार करा',
      plannedBy: '{{name}} यांनी नियोजित',
      printHeader: 'एक दिवसाची वारसा योजना',
      printFooter: 'महाराष्ट्र वारसा प्रवास योजना',
    },

    wadaDetail: {
      notFound: 'वाडा सापडला नाही',
      notFoundHint: 'हा वाडा काढला असेल किंवा लिंक चुकीची असू शकते.',
      backToWadas: 'सर्व वाड्यांकडे परत जा',
      heritageBadge: 'महाराष्ट्र वारसा • वाडा',
      about: 'याबद्दल',
      keyInformation: 'मुख्य माहिती',
      location: 'स्थान',
      era: 'कालखंड',
      bestTime: 'भेट देण्यासाठी सर्वोत्तम वेळ',
      district: 'जिल्हा',
      onMap: 'नकाशावर',
      keyFeatures: 'मुख्य वैशिष्ट्ये',
      quickInfo: 'चांगली माहिती',
    },

    // Common UI Elements
    ui: {
      loading: 'लोड होत आहे...',
      error: 'त्रुटी',
      success: 'यश',
      cancel: 'रद्द करा',
      confirm: 'पुष्टी करा',
      save: 'जतन करा',
      edit: 'संपादित करा',
      delete: 'हटवा',
      close: 'बंद करा',
      next: 'पुढील',
      previous: 'आधीचे',
      submit: 'सबमिट करा',
      reset: 'रीसेट करा',
      search: 'शोधा',
      filter: 'फिल्टर',
      sort: 'क्रमवारी',
      view: 'पहा',
      share: 'शेअर करा',
      download: 'डाउनलोड करा',
      upload: 'अपलोड करा',
      login: 'लॉगिन',
      logout: 'लॉगआउट',
      signup: 'साइन अप',
      profile: 'प्रोफाइल',
      settings: 'सेटिंग्ज',
      help: 'मदत',
      about: 'बद्दल',
      contact: 'संपर्क',
      privacy: 'गोपनीयता',
      terms: 'अटी',
      cookie: 'कुकी धोरण',
      language: 'भाषा',
      theme: 'थीम',
      darkMode: 'डार्क मोड',
      lightMode: 'लाइट मोड',
      notifications: 'सूचना',
      dashboard: 'डॅशबोर्ड',
      home: 'होम',
      back: 'मागे',
      menu: 'मेनू',
      more: 'अधिक',
      less: 'कमी',
      showAll: 'सर्व दाखवा',
      hideAll: 'सर्व लपवा',
      selectAll: 'सर्व निवडा',
      deselectAll: 'सर्व निवड रद्द करा',
      clear: 'साफ करा',
      apply: 'लागू करा',
      refresh: 'रिफ्रेश करा',
      update: 'अपडेट करा',
      create: 'तयार करा',
      new: 'नवीन',
      add: 'जोडा',
      remove: 'काढा',
      routes: 'मार्ग',
      close: 'बंद करा'
    }
  }
};

deepMerge(translationsData.en, localeEn);
deepMerge(translationsData.mr, localeMr);

export const translations = translationsData;
